import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CardItem from './CardItem';
import CardItemDTO from '@src/adapters/dto/card/CardItemDTO';
import { ReactComponent as Bladesicon } from '@src/assets/icons/categories/blades.svg';
import { ReactComponent as Consumables } from '@src/assets/icons/categories/consumables.svg';
import { ReactComponent as Dressings } from '@src/assets/icons/categories/dressings.svg';
import { ReactComponent as Fluids } from '@src/assets/icons/categories/fluids.svg';
import { ReactComponent as Hypodermics } from '@src/assets/icons/categories/hypodermics.svg';
import { ReactComponent as Infiltration } from '@src/assets/icons/categories/infiltration.svg';
import { ReactComponent as InstrumentSets } from '@src/assets/icons/categories/instrument_sets.svg';
import { ReactComponent as SkinPreparation } from '@src/assets/icons/categories/skin_preparation.svg';
import { ReactComponent as SupplementarySets } from '@src/assets/icons/categories/supplementary_sets.svg';
import { ReactComponent as Sutures } from '@src/assets/icons/categories/sutures.svg';
import { ReactComponent as TableExtras } from '@src/assets/icons/categories/table_extras.svg';

import theme from '@src/theme';
import CategoryHeaderDTO from '@src/adapters/dto/category/CategoryHeaderDTO';
import { CardModificationDTO } from '@src/adapters/dto/card/CardModificationDTO';
interface Props {
  cardId: string;
  category: CategoryHeaderDTO;
  collapsed: boolean;
  items: CardItemDTO[];
  isEdit: boolean;
  onItemDeleted(id: string, modification: CardModificationDTO): void;
  onItemUpdated(cardItem: CardItemDTO, modification: CardModificationDTO): void;
}

function Category({
  cardId,
  category,
  collapsed,
  items,
  isEdit,
  onItemDeleted,
  onItemUpdated,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(!collapsed);
  }, [collapsed]);

  const iconBackgroundColor = useMemo(() => {
    switch (category.name) {
      case 'Trays & Instrument Sets':
        return 'rgb(255, 231, 242)';
      case 'Supplementary Instruments':
        return 'rgba(255, 231, 242, 1)';
      case 'Sutures':
        return 'rgba(228, 218, 252, 1)';
      case 'Dressings':
        return '#FFF4E3';
      case 'Blades':
        return '#FFF4E3';
      case 'Hypodermics':
        return '#FFEDE3';
      case 'Fluids':
        return '#DAF9FE';
      case 'Infiltration':
        return '#DAF9FE';
      case 'Consumables':
        return 'rgba(222, 247, 228, 1)';
      case 'Table Extras':
        return '#FFEDE3';
      case 'Skin Preparation':
      default:
        return 'rgba(228, 218, 252, 1)';
    }
  }, [category.name]);

  const icon = useMemo(() => {
    switch (category.name) {
      case 'Trays & Instrument Sets':
        return <InstrumentSets />;
      case 'Supplementary Instruments':
        return <SupplementarySets />;
      case 'Sutures':
        return <Sutures />;
      case 'Dressings':
        return <Dressings />;
      case 'Blades':
        return <Bladesicon />;
      case 'Hypodermics':
        return <Hypodermics />;
      case 'Fluids':
        return <Fluids />;
      case 'Infiltration':
        return <Infiltration />;
      case 'Consumables':
        return <Consumables />;
      case 'Table Extras':
        return <TableExtras />;
      case 'Skin Preparation':
      default:
        return <SkinPreparation />;
    }
  }, [category]);

  return (
    <>
      <Box
        sx={{
          height: theme.spacing(7),
          display: 'flex',
          alignItems: 'center',
          borderTop: '1px solid #E6E7E9',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            spacing: theme.spacing(1),
          }}>
          <Box
            sx={(theme) => ({
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(1),
              padding: theme.spacing(1),
              backgroundColor: iconBackgroundColor,
              borderRadius: theme.spacing(1),
              display: 'flex',
            })}>
            {icon}
          </Box>
          <Box sx={{ width: '325px' }}>
            {items && items[0] && (
              <Typography variant="h6">
                {items[0].category.name} ({items.length})
              </Typography>
            )}
          </Box>
        </Box>
        {expanded && (
          <ExpandLessIcon
            sx={(theme) => ({
              marginLeft: '16px',
              marginRight: '24px',
              width: '24px',
              height: '24px',
              color: theme.palette.action.active,
              cursor: 'pointer',
            })}
            onClick={() => setExpanded(false)}
          />
        )}
        {!expanded && (
          <ExpandMoreIcon
            sx={(theme) => ({
              marginLeft: '16px',
              marginRight: '24px',
              color: theme.palette.action.active,
              cursor: 'pointer',
            })}
            onClick={() => setExpanded(true)}
          />
        )}
      </Box>
      {expanded &&
        items.map((item) => (
          <CardItem
            cardId={cardId}
            item={item}
            key={item.id}
            isEdit={isEdit}
            onItemDeleted={onItemDeleted}
            onItemUpdated={onItemUpdated}
          />
        ))}
    </>
  );
}

export default Category;
